import * as React from 'react';

/**
 * Returns true when any descendant of `containerRef` matching `childSelector`
 * has scrollWidth > clientWidth. Re-measures on two independent triggers:
 *   1. ResizeObserver on the container — any layout change of the host.
 *   2. The caller-supplied `deps` array — React-state changes that alter the
 *      children set without resizing the host (e.g. a flex child being added
 *      inside an overflow-hidden sibling that absorbs vertical growth).
 */
export function useElementOverflow(
  containerRef: React.RefObject<Element | null>,
  childSelector: string,
  deps: React.DependencyList = []
): boolean {
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      const children = el.querySelectorAll(childSelector);
      const overflow = Array.from(children).some((child) => child.scrollWidth > child.clientWidth);
      setIsOverflowing(overflow);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => observer.disconnect();
    // containerRef is a stable React ref object; childSelector is a string literal.
    // deps is spread in from the caller — linter can't analyze dynamic deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, childSelector, ...deps]);

  return isOverflowing;
}
